// src/pages/InventoryManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, Search, Settings2, Download, PackagePlus, Store, Eye } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { inventoryApi, productApi, storeApi } from '../api';
import InventoryDialog from '../components/products/InventoryDialog';
import ProductRestockDialog from '../components/admin/ProductRestockDialog';
import { exportToCSV } from '../utils/csvExport';

// 到期状态：过期/临期（7天内）/正常，决定行的颜色
function getExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 7) return 'soon';
  return 'ok';
}

const EXPIRY_ROW_CLASS = {
  expired: 'bg-red-50',
  soon:    'bg-amber-50',
  ok:      '',
  none:    '',
};
const EXPIRY_BADGE = {
  expired: <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">已过期</span>,
  soon:    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">临期</span>,
  ok:      null,
  none:    null,
};

const SOURCE_LABEL = { purchase: '进货', transfer_in: '划拨' };

const InventoryManagementPage = () => {
  const { currentUser } = useApp();
  // 批量进货权限对齐单条进货——总店和分店的admin/manager都能用，不限总店
  const canRestock = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const isHQ = currentUser?.store_type === 'headquarters';

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  // 总店查看门店库存——下拉框选门店，默认看自己（总店自己）。
  // 分店账号完全不会看到这个下拉框，行为和以前一样，只查自己
  const [stores, setStores] = useState([]);
  const [viewStoreId, setViewStoreId] = useState(currentUser?.store_id || '');
  // 是不是正在看"别的门店"（不是自己）——是的话，所有操作类按钮要禁用，
  // 库存的实际调整应该由那个门店自己发起，总店只能看、不能替分店改
  const isViewingOtherStore = isHQ && viewStoreId && String(viewStoreId) !== String(currentUser?.store_id);

  // 触发库存调整用：先搜商品，选中之后打开 InventoryDialog
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState(null); // 打开 InventoryDialog 用
  const [showRestockDialog, setShowRestockDialog] = useState(false);

  useEffect(() => {
    if (isHQ) {
      storeApi.getList().then(res => {
        if (res.success) setStores(res.data || []);
      }).catch(err => console.error('加载门店列表失败:', err));
    }
  }, [isHQ]);

  useEffect(() => {
    loadBatches();
  }, [expiringOnly, viewStoreId]);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const params = expiringOnly ? { expiring_only: '1' } : {};
      // 只有总店、且选的不是自己的时候，才带上 store_id 参数——
      // 分店账号即使这个state有值，也不传（后端本来也会忽略，但前端不传更清楚意图）
      if (isHQ && viewStoreId) params.store_id = viewStoreId;
      const res = await inventoryApi.getBatchOverview(params);
      if (res.success) setBatches(res.data || []);
    } catch (err) {
      console.error('加载批次总览失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (kw) => {
    setProductSearch(kw);
    if (!kw) { setProducts([]); return; }
    try {
      const res = await productApi.getList({ search: kw });
      if (res.success) setProducts(res.data || []);
    } catch (err) { console.error(err); }
  };

  const filteredBatches = batches.filter(b => {
    if (!searchKeyword) return true;
    const kw = searchKeyword.toLowerCase();
    return b.product_name?.toLowerCase().includes(kw) || b.barcode?.includes(kw) || b.batch_no?.toLowerCase().includes(kw);
  });

  const expiredCount = batches.filter(b => getExpiryStatus(b.expiry_date) === 'expired').length;
  const soonCount = batches.filter(b => getExpiryStatus(b.expiry_date) === 'soon').length;

  // 汇总卡片——跟着当前筛选结果走（搜索关键词/只看过期临期），不是永远统计全部，
  // 保持和下面表格实际看到的内容一致
  const totalBatchCount = filteredBatches.length;
  const totalQuantity = filteredBatches.reduce((sum, b) => sum + (b.quantity || 0), 0);
  const totalCostValue = filteredBatches.reduce((sum, b) => sum + (b.quantity || 0) * parseFloat(b.cost_price || 0), 0);
  // 商品种类：按 product_id 去重，不用商品名称去重（万一有重名商品会数错）
  const distinctProductCount = new Set(filteredBatches.map(b => b.product_id)).size;
  // 整箱数量：只累加"支持整箱"的批次（box_quantity有填），换算规则和表格里单行的箱数一致，
  // 只算凑得满整箱的部分
  const totalFullBoxes = filteredBatches.reduce((sum, b) => {
    if (b.box_quantity && b.box_quantity > 0) {
      return sum + Math.floor(b.quantity / b.box_quantity);
    }
    return sum;
  }, 0);
  // 散货数量：凑不满一箱的零头 + 压根不支持整箱的商品（这些天然全部都是散货）
  const totalLooseQuantity = filteredBatches.reduce((sum, b) => {
    if (b.box_quantity && b.box_quantity > 0) {
      return sum + (b.quantity % b.box_quantity);
    }
    return sum + (b.quantity || 0);
  }, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">库存管理</h2>
          <p className="text-xs text-gray-600 mt-1">批次总览、进货/损耗/盘点等库存调整</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => {
            exportToCSV(
              filteredBatches.map(b => ({
                ...b,
                expiry_status: getExpiryStatus(b.expiry_date) === 'expired' ? '已过期'
                  : getExpiryStatus(b.expiry_date) === 'soon' ? '临期' : '正常',
                source_label: SOURCE_LABEL[b.source_type] || b.source_type,
              })),
              [
                { key: 'product_name', label: '商品名称' },
                { key: 'barcode', label: '条码' },
                { key: 'batch_no', label: '批次号' },
                { key: 'quantity', label: '剩余数量' },
                { key: 'cost_price', label: '成本价' },
                { key: 'expiry_date', label: '到期日期' },
                { key: 'expiry_status', label: '到期状态' },
                { key: 'source_label', label: '来源' },
              ],
              '批次总览'
            );
          }}
            disabled={filteredBatches.length === 0}
            className="flex items-center gap-1.5 border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
            <Download className="w-4 h-4" />导出
          </button>
          {/* 查看别的门店时，库存调整/批量进货这两个操作按钮直接不显示——
              库存的实际调整应该由那个门店自己发起，总店只能看，不能替分店改 */}
          {!isViewingOtherStore && (
            <>
              <button onClick={() => setShowProductPicker(true)}
                className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
                <Settings2 className="w-4 h-4" />库存调整
              </button>
              {canRestock && (
                <button onClick={() => setShowRestockDialog(true)}
                  className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
                  <PackagePlus className="w-4 h-4" />批量进货
                </button>
              )}
            </>
          )}
          {/* 门店切换下拉框——只有总店账号能看到，放在按钮组最右侧 */}
          {isHQ && (
            <div className="relative">
              <Store className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select value={viewStoreId} onChange={e => setViewStoreId(e.target.value)}
                className="pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white appearance-none">
                {stores.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{String(s.id) === String(currentUser?.store_id) ? '（本店）' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* 汇总统计卡片——批次数/商品种类/总个数/整箱+零头/库存成本总价值，跟着当前筛选结果走 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">批次数</p>
          <p className="text-2xl font-bold mt-1">{totalBatchCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">商品种类</p>
          <p className="text-2xl font-bold mt-1">{distinctProductCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">总个数</p>
          <p className="text-2xl font-bold mt-1">{totalQuantity.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">整箱（含零头）</p>
          <p className="text-2xl font-bold mt-1">
            {totalFullBoxes.toLocaleString()}箱
            {totalLooseQuantity > 0 && <span className="text-base font-normal text-gray-500"> + {totalLooseQuantity.toLocaleString()}个</span>}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-xs text-gray-500">库存成本总价值</p>
          <p className="text-2xl font-bold mt-1">${totalCostValue.toFixed(2)}</p>
        </div>
      </div>

      {/* 正在查看别的门店时的只读提示条 */}
      {isViewingOtherStore && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <Eye className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            正在查看 <span className="font-medium">{stores.find(s => String(s.id) === String(viewStoreId))?.name}</span> 的库存（只读）——库存调整需要由该门店自己操作
          </p>
        </div>
      )}

      {/* 提醒条：有过期/临期批次时显示 */}
      {(expiredCount > 0 || soonCount > 0) && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            {expiredCount > 0 && <span className="font-medium">{expiredCount} 个批次已过期</span>}
            {expiredCount > 0 && soonCount > 0 && '，'}
            {soonCount > 0 && <span className="font-medium">{soonCount} 个批次7天内到期</span>}
            ，建议尽快处理
          </p>
        </div>
      )}

      {/* 搜索 + 筛选 */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="搜索商品名称/条码/批次号"
            value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
        <button onClick={() => setExpiringOnly(!expiringOnly)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            expiringOnly ? 'bg-amber-500 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}>
          只看过期/临期
        </button>
      </div>

      {/* 批次总览表格 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">{expiringOnly ? '没有过期/临期的批次' : '暂无批次记录'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-gray-500 text-left">
                <th className="px-4 py-3">商品</th>
                <th className="px-4 py-3">批次号</th>
                <th className="px-4 py-3 text-right">剩余数量</th>
                <th className="px-4 py-3 text-right">箱容</th>
                <th className="px-4 py-3 text-right">箱数</th>
                <th className="px-4 py-3 text-right">成本价</th>
                <th className="px-4 py-3">到期日期</th>
                <th className="px-4 py-3">来源</th>
              </tr>
            </thead>
            <tbody>
              {filteredBatches.map(b => {
                const status = getExpiryStatus(b.expiry_date);
                // 箱数按 剩余数量 ÷ 每箱数量 换算，不是整箱的部分单独标出来，
                // 不支持整箱的商品（box_quantity 没填）这一列直接显示"—"
                const hasBoxOption = b.box_quantity && b.box_quantity > 0;
                const fullBoxes = hasBoxOption ? Math.floor(b.quantity / b.box_quantity) : 0;
                const remainder = hasBoxOption ? b.quantity % b.box_quantity : 0;
                return (
                  <tr key={b.id} className={`border-t border-gray-100 ${EXPIRY_ROW_CLASS[status]}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{b.product_name}</p>
                      <p className="text-xs text-gray-400">{b.barcode}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{b.batch_no}</td>
                    <td className="px-4 py-3 text-right font-medium">{b.quantity}</td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      {hasBoxOption ? `${b.box_quantity}/箱` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {hasBoxOption
                        ? `${fullBoxes}箱${remainder > 0 ? ` + ${remainder}个` : ''}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">${parseFloat(b.cost_price).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={status === 'expired' ? 'text-red-600 font-medium' : status === 'soon' ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                          {b.expiry_date || '—'}
                        </span>
                        {EXPIRY_BADGE[status]}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{SOURCE_LABEL[b.source_type] || b.source_type}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 选商品，触发库存调整 */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="font-bold text-lg">选择要调整的商品</h3>
            </div>
            <div className="p-6 space-y-3">
              <input type="text" placeholder="搜索商品名称/条码" value={productSearch}
                onChange={e => searchProducts(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
              <div className="max-h-64 overflow-y-auto space-y-1">
                {products.map(p => (
                  <button key={p.id}
                    onClick={() => { setAdjustProduct(p); setShowProductPicker(false); setProductSearch(''); setProducts([]); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200">
                    <p className="text-sm font-medium text-gray-800">{p.name}</p>
                    <p className="text-xs text-gray-400">{p.barcode} · 库存 {p.stock}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end p-6 border-t">
              <button onClick={() => { setShowProductPicker(false); setProductSearch(''); setProducts([]); }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">取消</button>
            </div>
          </div>
        </div>
      )}

      {adjustProduct && (
        <InventoryDialog
          product={adjustProduct}
          onClose={() => setAdjustProduct(null)}
          onSuccess={loadBatches}
        />
      )}

      {showRestockDialog && canRestock && (
        <ProductRestockDialog
          onClose={() => setShowRestockDialog(false)}
          onImported={loadBatches}
        />
      )}
    </div>
  );
};

export default InventoryManagementPage;